import isEqual from 'lodash/isEqual';
import {produce} from 'immer';
import {VNode} from 'snabbdom';
import {jsx} from 'panel';

import {SelectEl} from 'common/components/select';
import {ChartType, ReportName, SavedLegend, ValueRepresentationType} from 'common/report/constants';
import {ReportVisualization} from 'common/report/report-manager/visualization';
import {isPeopleTimeSeries} from 'common/report/insights/util';
import {ChartResultChartTypes, MultiMetricChartResultChartTypes} from 'common/report/insights/util/constants';
import {getSelectedSegments} from 'common/report/insights/util/segments';
import {getDateRange} from 'common/report/insights/util/time';
import {
  AnalysisType,
  DisplayOptions,
  InsightsBookmarkParams,
  InsightsQueryError,
  InsightsQueryResults,
} from 'common/report/insights/util/types';
import {isLiftEnabled} from 'common/report/util/lift';
import {getOwnAttr} from 'common/util';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {ChartHeaderUpdateDetail} from 'common/widgets/chart-header';
import {OrderedStandardTimeUnits, TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {ChartHeaderAttrs} from 'common/widgets/chart-header/types';
import {propagateEvent} from 'common/util/dom';
import {SegmentFilterDataTableJsxProps} from 'common/charts/misc/segment-filter-data-table';
import {QueryReason} from 'common/report/util/query-tracking/types';
import {ColumnResizeEvent, SortChangeEvent} from 'common/components/data-table/types';
import {DataSortMenuEl} from 'common/widgets/data-sort-menu';
import {ReportStatus} from 'common/widgets/warning-hero/constants';
import {canUseViewN, isPivotTableEnabled} from 'common/components/data-table/utils';
import {SortBy, SortConfig} from 'common/report/util/sorting/types';
import {VIEW_N_DEFAULT_LIMIT} from 'common/components/data-table/constants';
import {showTour, TourId} from 'common/util/chameleon';
import {
  FeatureRolloutName,
  FeatureRollouts,
  useReportConsistency,
  useSingleReport,
} from 'common/report/util/feature-sampling';
import {SelectTheme} from 'common/components/select/constants';
import {TableToggleableMetrics} from 'common/report/funnels/models/constants';
import {
  TABLE_COLUMN_SELECTOR_DISPLAY_NAME,
  TABLE_COLUMN_SELECTOR_TOOLTIPS,
} from 'common/report/funnels/models/constants';
import {DisplaySize, Placement} from 'common/components/tooltip/types';
import {
  canUseCountUsersOnceCreateCohort,
  canUseCountUsersOnceViewUsers,
  canUseMultiAttributionCreateCohort,
  canUseMultiAttributionFilter,
  canUseMultiAttributionViewUsers,
} from 'common/widgets/multi-attribution-menu/utils';
import {getFormattedRowData, hasRetentionData} from 'common/report/retention/util/parsing';
import {RetentionBookmarkType} from 'common/report/retention/charts/types';

import {Report} from '../models/report';
import {isFunnelOnlyInsightsBookmark, isRetentionOnlyInsightsBookmark} from '../models/utils';
import {InsightsRetentionTableEl} from '../charts/insights-retention-table-chart';
import {InsightsChartDisplayEl} from './charts/chart-display';
import {
  bookmarkUpdatesFromAnalysisChange,
  bookmarkUpdatesFromHeaderUpdate,
  correctDisplayOptions,
  getAnalysisMenuItems,
  getChartTypeSectionOverrides,
  getLeafLevelSegments,
  getRollingOptions,
  getSelectedAnalysis,
  getSelectedAnalysisIcon,
  getSelectedChartType,
  getSelectedRollingOption,
  getTimeComparisonDisabledReason,
  getTimeComparisonDisabledReasonMessage,
  getTimeUnitSectionOverrides,
  getValueRepresentations,
  shouldRequeryDisplayOptions,
} from './util';
import {getDataTableTrackName} from './charts/chart-display/utils';

export class InsightsVisualization extends ReportVisualization<
  InsightsBookmarkParams,
  InsightsQueryResults,
  InsightsQueryError
> {
  reportName = ReportName.Insights;
  get defaultState() {
    return {
      // TODO remove this once table column widths are persisted to bookmark
      tableChartColumnWidthOverrides: {},
    };
  }

  dataTableChartEnabled() {
    return isPivotTableEnabled(this.project);
  }

  chartDisplay() {
    return (
      <InsightsChartDisplayEl
        attrs={{
          'resizing-layout': this.attr(`show-resizing-layout`),
          'rca-source-id': this.attr(`rca-source-id`),
          elref: `chartDisplay`,
          'enable-pivot-view': this.dataTableChartEnabled(),
          'can-use-view-n': canUseViewN(this.project),
          'can-use-attribution-filter': canUseMultiAttributionFilter(this.project),
          'can-use-attribution-view-users': canUseMultiAttributionViewUsers(this.project),
          'can-use-count-users-once-view-users': canUseCountUsersOnceViewUsers(this.project),
          'can-use-attribution-create-cohort': canUseMultiAttributionCreateCohort(this.project),
          'can-use-count-users-once-create-cohort': canUseCountUsersOnceCreateCohort(this.project),
        }}
        props={{
          bookmarkParams: this.bookmarkParams,
          queryResults: this.results,
          queryError: null,
          dataGroups: this.dataGroups,
          tableChartColumnWidthOverrides: this.state.tableChartColumnWidthOverrides,
        }}
        on={{
          'export-csv': (ev) => propagateEvent(this, ev),
          update: ({detail}) => {
            let requery = true;

            // chart updates will not have changes alongside sorting or columnWidths,
            // so safe to assume we don't have to query when present.
            const {sorting, columnWidths} = detail.delta;
            if (sorting || columnWidths) {
              requery = false;
            }

            this.dispatchUpdate({
              params: {...this.bookmarkParams, ...detail.delta},
              requery,
            });
          },
          columnResize: (ev) => {
            this.handleDataTableColumnResize(ev);
          },
        }}
      />
    );
  }

  // override data table template for custom retention table
  dataTableTemplate(): VNode {
    const chartType = this.bookmarkParams.displayOptions.chartType;
    if (isRetentionOnlyInsightsBookmark(this.bookmarkParams) && chartType === ChartType.RetentionCurve) {
      const data = Object.values(this.results.series)[0];
      const incompleteBuckets = this.results.meta.report_sections.show[0].incomplete_buckets;
      const segments = getSelectedSegments(Report.fromBookmarkData(this.bookmarkParams).sections.group.clauses);
      const tableData = getFormattedRowData(data, incompleteBuckets, segments, this.project?.timezone);
      const rowLimit = this.attr(`is-download-mode`) ? 30 : 255;
      const rowData = tableData.slice(0, rowLimit);
      const tableHasData = hasRetentionData(tableData);
      if (!tableHasData) {
        return null;
      }
      return (
        <InsightsRetentionTableEl
          sel='.retention-curve-table'
          params={{
            rowData,
            bookmarkData: {
              bookmark: this.bookmarkParams,
              queryResults: this.results,
              type: RetentionBookmarkType.MULTIMETRIC,
            },
            projectId: this.attr(`project-id`),
            projectTimezone: this.project?.timezone,
            resizingLayout: this.attr(`show-resizing-layout`),
            onSortChange: (sorting: SortConfig) => {
              this.dispatchUpdate({
                params: {
                  ...this.bookmarkParams,
                  sorting: {
                    ...this.bookmarkParams.sorting,
                    [ChartType.RetentionCurve]: sorting,
                  },
                },
                requery: false,
              });
            },
            onExpandedSegmentsChange: (expandedSegments: string[]) =>
              this.handleExpandedSegmentsChange(expandedSegments),
          }}
        />
      );
    }

    return super.dataTableTemplate();
  }

  reportSpecificDataTableAttrs(): SegmentFilterDataTableJsxProps['attrs'] {
    const trackName = getDataTableTrackName(this.bookmarkParams.displayOptions.chartType);
    return {'track-name': trackName};
  }

  reportSpecificResizingLayoutAttrs() {
    if (this.bookmarkParams?.displayOptions?.chartType === ChartType.Table) {
      return {'disable-content-padding': true};
    }
    return {};
  }

  reportSpecificChartExtras() {
    if (this.bookmarkParams?.displayOptions?.chartType === ChartType.FunnelSteps) {
      const items = Object.values(TableToggleableMetrics).map((column: TableToggleableMetrics) => ({
        isSelected: this.bookmarkParams.displayOptions.funnelStepsSelectedTableColumns?.[column],
        label: TABLE_COLUMN_SELECTOR_DISPLAY_NAME[column],
        tooltip: {
          text: TABLE_COLUMN_SELECTOR_TOOLTIPS[column],
          placement: Placement.Left,
          size: DisplaySize.Small,
        },
        value: column,
        ...(column === TableToggleableMetrics.StatSig
          ? {isDisabled: this.bookmarkParams.sections.group.length === 0} // TODO event comparisons?
          : {}),
      }));
      return (
        <SelectEl
          attrs={{
            elref: `funnelStepMetricSelector`,
            icon: {leftIcon: `settings`},
            'icon-only': true,
            theme: SelectTheme.Secondary,
            multiple: true,
            'menu-width': 270,
            tooltip: {text: `Pick which metrics are shown in the table below.`},
          }}
          props={{
            sections: [{items}],
          }}
          on={{
            change: (ev) => this.updateFunnelStepColumns(ev),
          }}
        />
      );
    }
    return null;
  }
  updateFunnelStepColumns(ev) {
    const column = ev.detail.item.value;
    const defaultTableColumns = Object.fromEntries(Object.values(TableToggleableMetrics).map((key) => [key, false])); // TODO set this up when selecting the chart
    const tableColumns = this.bookmarkParams.displayOptions.funnelStepsSelectedTableColumns || defaultTableColumns;
    const funnelStepsSelectedTableColumns = {
      ...tableColumns,
      [column]: !tableColumns[column],
    } as DisplayOptions[`funnelStepsSelectedTableColumns`];
    this.updateDisplayOptions({...this.bookmarkParams.displayOptions, funnelStepsSelectedTableColumns});
  }

  getBookmarkParamsFromSortChange(ev: SortChangeEvent): InsightsBookmarkParams {
    let {chartType} = this.bookmarkParams.displayOptions;
    if (chartType === ChartType.FunnelSteps) {
      chartType = ChartType.Bar;
    }
    let sortConfig = ev.detail.sorting;
    if (
      canUseViewN(this.project) &&
      chartType === ChartType.Table &&
      this.bookmarkParams.sorting[chartType].sortBy !== sortConfig.sortBy
    ) {
      // Use default viewNLimits when SortBy changes
      sortConfig = produce(sortConfig, (draft) => {
        if (draft.sortBy === SortBy.Value) {
          draft.viewNLimit = VIEW_N_DEFAULT_LIMIT;
        } else {
          draft.colSortAttrs.forEach((colSortAttr) => {
            colSortAttr.viewNLimit = VIEW_N_DEFAULT_LIMIT;
          });
        }
      });
    }

    return {
      ...this.bookmarkParams,
      sorting: {
        ...this.bookmarkParams.sorting,
        [chartType]: sortConfig,
      },
    };
  }

  getMultimetricChartTypes() {
    let chartTypes = [...MultiMetricChartResultChartTypes];
    if (isFunnelOnlyInsightsBookmark(this.bookmarkParams)) {
      chartTypes = [ChartType.FunnelSteps, ChartType.Line, ChartType.Bar, ChartType.Metric, ChartType.FunnelTopPaths];
    }
    if (isRetentionOnlyInsightsBookmark(this.bookmarkParams)) {
      chartTypes = [ChartType.RetentionCurve, ChartType.Line, ChartType.Metric];
    }
    return chartTypes;
  }

  reportSpecificHeaderAttrs(): Partial<ChartHeaderAttrs> {
    // TODO calculate attrs directly from bookmark params
    // for now, use the Report model for parity with Insights app
    const report = Report.fromBookmarkData(this.bookmarkParams);
    const timeComparisonDisabledReason = getTimeComparisonDisabledReason(report); // TODO overrides
    const breakdowns = getSelectedSegments(report.sections.group.clauses, false);
    const enableLiftComparison = isLiftEnabled();

    // TODO hopefully some of these can be shared among different report types
    return {
      breakdowns,
      'chart-type-overrides': getChartTypeSectionOverrides({
        bookmarkParams: report.toBookmarkData(),
        metricsResource: this.getContext(`metrics`),
      }),
      'chart-types':
        useSingleReport() || useReportConsistency() ? this.getMultimetricChartTypes() : ChartResultChartTypes,
      'comparison-date-range': report.timeComparison,
      'date-range': getDateRange(report.sections.time.clauses).getAttributes(),
      'date-unit-overrides': getTimeUnitSectionOverrides(report),
      'date-units':
        report.showTimeUnitAggregation() && OrderedStandardTimeUnits.filter((value) => value !== TimeUnit.Year),
      'selected-chart-type': getSelectedChartType(report),
      'selected-date-unit': report.timeUnit(),
      'show-date-range-selector':
        !report.sections.show.isPeopleOnlyQuery() || !isPeopleTimeSeries(this.bookmarkParams.sections),
      'time-comparison-disabled-reason': getTimeComparisonDisabledReasonMessage(report, timeComparisonDisabledReason),

      // lift-specific attrs
      'enable-lift-comparison': enableLiftComparison,
      'selected-breakdown-segments': getLeafLevelSegments({
        breakdowns,
        queryResults: this.results,
        report,
      }),
      'selected-value-representation':
        (report.displayOptions && report.displayOptions.value) || ValueRepresentationType.Absolute,
      'value-representations': getValueRepresentations(enableLiftComparison, report.displayOptions),
      'selected-lift-comparison': report.liftComparison,
      'lift-type-overrides': report.getLiftTypeOverrides(),
    };
  }

  protected headerChildrenTemplate() {
    // Analysis type menu (linear, rolling, etc)
    const report = Report.fromBookmarkData(this.bookmarkParams);
    const sections = getAnalysisMenuItems({report, currentProject: this.project});

    const sortControls = this.dataTableChartEnabled() &&
      report.displayOptions.chartType === ChartType.Table &&
      (Boolean(this.attr(`status`)) === false || this.attr(`status`) === ReportStatus.Loading) && (
        <>
          <DataSortMenuEl
            attrs={{
              slot: `report-controls`,
            }}
            params={{
              isLoading: this.attr(`status`) === ReportStatus.Loading,
              sortConfig: this.getContext(`segmentFilterController`)?.tableSortConfig,
              defaultSortConfig: this.getContext(`segmentFilterController`)?.defaultSortConfig,
              columns: this.getContext(`segmentFilterController`)?.getSections()[0]?.tableColumns,
              chartType: this.bookmarkParams.displayOptions.chartType,

              onSortChange: (ev: SortChangeEvent) => {
                this.dispatchUpdate({
                  params: this.getBookmarkParamsFromSortChange(ev),
                  requery: false,
                });
              },
            }}
          />
        </>
      );

    const analysisControls = sections && (
      <>
        {report.getDisplayAnalysis() === AnalysisType.Rolling && (
          <div sel='.chart-control-extra'>
            <SelectEl
              attrs={{
                'selected-option': getSelectedRollingOption(report),
                sections: getRollingOptions(report),
              }}
              on={{
                change: (ev) => this.handleRollingWindowChange(ev.detail.item.value),
              }}
            />
          </div>
        )}
        <div sel='.chart-control'>
          <SelectEl
            attrs={{
              'selected-option': getSelectedAnalysis(report),
              icon: {leftIcon: getSelectedAnalysisIcon(report)},
              'menu-width': 160,
              sections,
            }}
            on={{
              menuChange: (ev) => {
                if (
                  ev.detail.isOpen &&
                  FeatureRollouts[FeatureRolloutName.InsightsNewCountMenu].isEnabled(this.project)
                ) {
                  showTour(TourId.InsightsAnalysisMenu, true);
                }
              },
              change: (ev) => this.handleAnalysisSelectChange(ev.detail.item.value),
            }}
          />
        </div>
      </>
    );
    const extraControls = this.reportSpecificChartExtras();

    if (analysisControls || sortControls || extraControls) {
      return (
        <>
          {sortControls}
          <div sel='.chart-controls-extra' attrs={{slot: `chart-controls`}}>
            {analysisControls}
            {extraControls}
          </div>
        </>
      );
    } else {
      return null;
    }
  }

  updateDisplayOptions(options: DisplayOptions) {
    const oldDisplayOptions = this.bookmarkParams.displayOptions;
    const newDisplayOptions = correctDisplayOptions(options);

    if (!isEqual(oldDisplayOptions, newDisplayOptions)) {
      const requery = shouldRequeryDisplayOptions(oldDisplayOptions, newDisplayOptions);
      this.dispatchUpdate({
        params: {...this.bookmarkParams, displayOptions: newDisplayOptions},
        requery,
      });
    }
  }

  handleExpandedSegmentsChange(expandedSegments: string[]) {
    const savedLegend: SavedLegend = {
      ...this.bookmarkParams.legend?.individualSelectedSegments,
      expanded: expandedSegments.map((key) => ({key})),
    };
    this.dispatchUpdate({
      params: {
        ...this.bookmarkParams,
        legend: {
          ...this.bookmarkParams.legend,
          individualSelectedSegments: savedLegend,
        },
      },
      requery: false,
    });
  }

  handleRollingWindowChange(rollingWindowSize) {
    this.updateDisplayOptions({
      ...this.bookmarkParams.displayOptions,
      rollingWindowSize,
    });
  }

  handleAnalysisSelectChange(analysis: AnalysisType) {
    const {displayOptions} = bookmarkUpdatesFromAnalysisChange(this.bookmarkParams, analysis);
    this.updateDisplayOptions(displayOptions);
  }

  handleDataTableColumnResize(ev: ColumnResizeEvent) {
    this.update({tableChartColumnWidthOverrides: ev.detail.columnWidthOverrides});
  }

  bookmarkUpdateFromHeaderUpdate(update: ChartHeaderUpdateDetail) {
    const report = Report.fromBookmarkData(this.bookmarkParams);
    const enableLiftComparison = isLiftEnabled();
    const bookmarkUpdates = bookmarkUpdatesFromHeaderUpdate({
      report,
      currentProject: this.project,
      update,
      enableLiftComparison,
      hasChangedTimeUnit: this.state.hasChangedTimeUnit,
    });

    if (getOwnAttr(update.delta, `dateUnit`)) {
      this.update({hasChangedTimeUnit: true});
    }

    let requery = true;
    let hasChangedChartType = false;
    if (bookmarkUpdates.displayOptions) {
      requery = shouldRequeryDisplayOptions(this.bookmarkParams.displayOptions, bookmarkUpdates.displayOptions);
      const {chartType, plotStyle} = this.bookmarkParams.displayOptions;
      hasChangedChartType =
        chartType !== bookmarkUpdates.displayOptions.chartType ||
        plotStyle !== bookmarkUpdates.displayOptions.plotStyle;
    }

    const params = {...this.bookmarkParams, ...bookmarkUpdates};
    return {params, queryReason: QueryReason.QbOtherUpdate, requery, hasChangedChartType};
  }
}

export const InsightsVisualizationEl = defineAndMakeJsxFactory(`insights-visualization`, InsightsVisualization);
export const InsightsVisualizationReact = panel2react(`insights-visualization`, InsightsVisualization);
